import { queryOptions, useQuery } from "@tanstack/react-query";
import { publicAuthConfigQueryOptions } from "@/services/sso-service.ts";
import { DEFAULT_STATUS_PAGE_URL } from "@/shared/status-page.ts";

/**
 * The public status page, as the deployment names it (`STATUS_PAGE_URL`), so
 * moving the page is an env change rather than a release. Lives here rather
 * than in a component because two separate surfaces link to it — the in-app
 * status strip and the boot-failure screen, which renders before the router
 * exists — and a second copy is exactly how the two drift apart.
 *
 * Until the public auth-config read answers, or when it cannot because the
 * backend is what is failing, the shared default stands in: the boot-failure
 * screen must never be left without a link.
 */
export function useStatusPageUrl(): string {
	const { data } = useQuery(publicAuthConfigQueryOptions());
	return data?.statusPageUrl ?? DEFAULT_STATUS_PAGE_URL;
}

/**
 * Shape of the `/.well-known/status` document served by oneDeploy on the
 * service's own origin. Only the fields we consume are typed; the endpoint
 * carries additional latency-timeline / daily-history data we surface on the
 * public status page rather than in-app.
 */
export type ServiceSeverity = "warning" | "critical";

export interface ServiceIncidentEvent {
	id: string;
	timestamp: string;
	message: string;
}

export interface ServiceStatusIncident {
	id: string;
	title: string;
	severity: ServiceSeverity;
	/** oneDeploy lifecycle status — "closed" once resolved. */
	status: string;
	createdAt: string;
	/** Present once the incident is resolved. */
	closedAt?: string;
	/** Chronological updates posted while the incident is open. */
	events?: ServiceIncidentEvent[];
}

/** Live health rollup for the deployment. */
export interface ServiceHealth {
	status: "operational" | "degraded" | "down" | "unknown";
	/** Rolling 90-day uptime percentage, e.g. 99.9. */
	uptime90d?: number;
}

export interface ServiceStatus {
	domain: string;
	status: "operational" | "degraded" | "down" | "unknown";
	/** Nested health block; mirrors the top-level status but carries uptime. */
	service?: ServiceHealth;
	incidents?: ServiceStatusIncident[];
}

/**
 * Reads the deployment's own status from `${origin}/.well-known/status`.
 *
 * Served by the oneDeploy gateway on the service's own domain, so it is
 * same-origin (no CORS) and needs no configured URL. It is NOT part of the
 * oneAI backend OpenAPI spec, so it is fetched directly rather than via $api.
 *
 * Returns null when the endpoint is absent — e.g. on localhost (not behind the
 * gateway) or for an internal-only service (404) — so callers render nothing.
 */
export const serviceStatusQueryOptions = () =>
	queryOptions({
		queryKey: ["well-known-status"],
		queryFn: async ({ signal }): Promise<ServiceStatus | null> => {
			const response = await fetch(`${location.origin}/.well-known/status`, {
				signal,
				headers: { accept: "application/json" },
			});
			if (!response.ok) {
				return null;
			}
			return (await response.json()) as ServiceStatus;
		},
		// Poll so an incident surfaces (and clears) without a reload.
		refetchInterval: 60 * 1000,
		staleTime: 60 * 1000,
		retry: false,
	});

/**
 * An incident counts as ongoing only when it is neither flagged closed nor
 * stamped with a closing time. oneDeploy sets both on resolution, but we guard
 * on either so a half-written record can never leak a stale incident into the
 * UI.
 */
const isOngoing = (incident: ServiceStatusIncident): boolean =>
	incident.status !== "closed" && !incident.closedAt;

/** All in-progress incidents, most recent first. */
export const getOngoingIncidents = (
	status: ServiceStatus | null | undefined,
): ServiceStatusIncident[] =>
	(status?.incidents ?? [])
		.filter(isOngoing)
		.sort((a, b) => b.createdAt.localeCompare(a.createdAt));

/** The latest posted update for an incident, if any. */
export const latestIncidentEvent = (
	incident: ServiceStatusIncident,
): string | undefined =>
	incident.events
		?.slice()
		.sort((a, b) => b.timestamp.localeCompare(a.timestamp))[0]?.message;

/** Health rollup, preferring the nested block (it carries uptime). */
export const getServiceHealth = (
	status: ServiceStatus | null | undefined,
): ServiceHealth => ({
	status: status?.service?.status ?? status?.status ?? "unknown",
	uptime90d: status?.service?.uptime90d,
});

// ---------------------------------------------------------------------------
// Unified status model
//
// The customer-facing status strip has two independent sources — a super-admin
// manual notice and the oneDeploy incident feed — plus a live health rollup.
// `buildSystemStatus` merges them into a single severity-ranked view so the UI
// renders one bar (coloured by the worst active severity) rather than a stack.
// ---------------------------------------------------------------------------

export type SystemSeverity = ServiceSeverity;

const SEVERITY_RANK: Record<SystemSeverity, number> = {
	warning: 1,
	critical: 2,
};

/** A single normalised entry in the merged status list. */
export interface SystemNotice {
	id: string;
	source: "admin" | "incident";
	severity: SystemSeverity;
	title: string;
	/** Latest incident update, or the admin message body. */
	detail?: string;
	/** ISO timestamp: incident start, or admin activation. */
	time?: string;
}

export interface SystemStatusView {
	/** null when everything is operational and no notice is active. */
	severity: SystemSeverity | null;
	notices: SystemNotice[];
	health: ServiceHealth;
}

/** The active admin banner, already resolved to the viewer's locale. */
export interface AdminNoticeInput {
	enabled: boolean;
	message: string;
	activatedAt?: string;
	/**
	 * Admin-chosen tone. Optional so callers without the field still work;
	 * defaults to "critical" — a manual notice is a deliberate, high-trust
	 * signal, so it reads as an outage unless the admin softens it to a warning.
	 */
	severity?: SystemSeverity;
}

const worstSeverity = (notices: SystemNotice[]): SystemSeverity | null => {
	let worst: SystemSeverity | null = null;
	for (const notice of notices) {
		if (!worst || SEVERITY_RANK[notice.severity] > SEVERITY_RANK[worst]) {
			worst = notice.severity;
		}
	}
	return worst;
};

/**
 * Merge the admin notice and ongoing incidents into one severity-ranked view.
 * A degraded/down health status raises the bar on its own even when no notice
 * has been filed, so users learn something is off before an incident is opened.
 */
export const buildSystemStatus = (input: {
	admin: AdminNoticeInput | null | undefined;
	status: ServiceStatus | null | undefined;
}): SystemStatusView => {
	const health = getServiceHealth(input.status);
	const notices: SystemNotice[] = [];

	// Admin notice is pinned first — a human signal outranks auto-detection.
	if (input.admin?.enabled) {
		notices.push({
			id: "admin",
			source: "admin",
			severity: input.admin.severity ?? "critical",
			title: input.admin.message,
			time: input.admin.activatedAt,
		});
	}

	for (const incident of getOngoingIncidents(input.status)) {
		notices.push({
			id: incident.id,
			source: "incident",
			severity: incident.severity,
			title: incident.title,
			detail: latestIncidentEvent(incident),
			time: incident.createdAt,
		});
	}

	let severity = worstSeverity(notices);
	if (!severity) {
		// No explicit notice — fall back to raw health.
		if (health.status === "down") {
			severity = "critical";
		} else if (health.status === "degraded") {
			severity = "warning";
		}
	}

	return { severity, notices, health };
};

// ---------------------------------------------------------------------------
// Boot-failure classification
//
// When the session cannot be restored on app load, the user is owed an honest
// answer about WHOSE problem it is. The `/.well-known/status` document above is
// what makes that answerable: it is served by the oneDeploy gateway rather than
// by the oneAI backend, so it keeps answering through a database or app-node
// outage. If the gateway says the platform is down, we say so; if the gateway is
// healthy, our backend sits behind the very host that just replied, so the
// user's own network is the likelier culprit.
// ---------------------------------------------------------------------------

/** Whose problem a failed session restore most likely is. */
export type BootFailureKind = "platform_outage" | "connection" | "unknown";

/**
 * Classify a failed session restore from the gateway's own health document.
 *
 * `gatewayReachable` is deliberately separate from `status`: the fetch returns
 * `null` both when the gateway answered 404 (not behind oneDeploy at all — e.g.
 * localhost) and when it could not be reached, and those mean opposite things.
 * Only the caller knows which happened, so an unreachable gateway classifies as
 * `unknown` and the copy stays non-committal rather than blaming either side.
 */
export const classifyBootFailure = (input: {
	status: ServiceStatus | null | undefined;
	gatewayReachable: boolean;
}): BootFailureKind => {
	if (!input.gatewayReachable) {
		return "unknown";
	}
	const health = getServiceHealth(input.status);
	if (health.status === "down" || health.status === "degraded") {
		return "platform_outage";
	}
	if (health.status === "operational") {
		return "connection";
	}
	// The gateway replied but has no opinion (no status document, or a shape we
	// don't recognise). Reachability alone is not evidence about our backend.
	return "unknown";
};
